import { z } from "zod";
import type { CountryCode } from "libphonenumber-js/max";
import { UserRole } from "@/enum/common.enum";
import { DEFAULT_COUNTRY, isValidPhone } from "@/lib/phone";

const email = z.string().trim().min(1, "Email is required.").email("Enter a valid email address.");
// New-password strength floor: length + upper/lower/number/symbol. The backend's own floor
// is length-only (>=6, see routes/auth.py) — this is stricter by design, and safe to be:
// tightening only rejects weak passwords the server would still technically accept.
export const password = z.string()
  .min(1, "Password is required.")
  .min(8, "Password must be at least 8 characters.")
  .regex(/[a-z]/, "Password must include a lowercase letter.")
  .regex(/[A-Z]/, "Password must include an uppercase letter.")
  .regex(/[0-9]/, "Password must include a number.")
  .regex(/[^a-zA-Z0-9]/, "Password must include a symbol.");

// Login confirms a password the user already has — it may predate the strength
// rules above, so only presence is checked here, never strength.
export const existingPassword = z.string().min(1, "Password is required.");

export const loginSchema = z.object({ email, password: existingPassword });

export const registerSchema = z.object({
  email,
  password,
  name: z.string().trim().min(2, "Full name is required."),
  role: z.literal(UserRole.AGENT).default(UserRole.AGENT),
});

export const acceptInviteSchema = (existingUser: boolean) => z.object({
  name: existingUser ? z.string().optional().default("") : z.string().trim().min(2, "Your name is required."),
  password: existingUser ? existingPassword : password,
  // ^ existing users only re-enter their current password to confirm identity — any
  // non-empty value is valid here, unlike new-password fields which enforce min length.
});

export const forgotPasswordSchema = z.object({ email });

export const resetPasswordSchema = z.object({
  password,
  confirmPassword: password,
}).refine(({ password, confirmPassword }) => password === confirmPassword, {
  message: "Passwords don't match.",
  path: ["confirmPassword"],
});

export const phoneLoginSchema = z.object({
  phone: z.string().trim().min(1, "Mobile number is required."),
  /** Form-local, like the Settings field: only the resolved E.164 is sent. */
  phoneCountry: z.string().trim().min(2),
}).superRefine((values, ctx) => {
  if (!isValidPhone(values.phone, (values.phoneCountry || DEFAULT_COUNTRY) as CountryCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["phone"],
      message: "That isn't a valid number for the selected country.",
    });
  }
});

export type LoginValues = z.infer<typeof loginSchema>;
export type PhoneLoginValues = z.infer<typeof phoneLoginSchema>;
export type RegisterValues = z.infer<typeof registerSchema>;
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;
export type AcceptInviteValues = z.infer<ReturnType<typeof acceptInviteSchema>>;
